import React, { useEffect, useState } from 'react';
import './GeoBlockModal.css'; // We'll create this for styling

interface GeoBlockModalProps {
  onClose?: () => void;
}

type GeoResponse = {
  country: { iso_code: string };
  city: { names: { en: string } };
};

const disclaimerText: Record<string, string> = {
  London: "This website is not tailored for UK audiences and may not be designed in compliance with the UK Financial Promotions Regime. If you're visiting from the UK, we kindly request that you exit this site promptly.",
  Washington: "Unfortunately, the token sale is not available in your region",
  // You can add more countries or regions as needed
  Default: "Access to this website is restricted in your region due to regulatory requirements."
};

const GeoBlockModal: React.FC<GeoBlockModalProps> = ({ onClose }) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [location, setLocation] = useState<string>("Default");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check disclaimer acceptance first
    const disclaimerTimestamp = localStorage.getItem('disclaimerAccepted');
    if (disclaimerTimestamp) {
      const now = Date.now();
      const then = parseInt(disclaimerTimestamp);
      const hoursPassed = (now - then) / (1000 * 60 * 60);
      
      if (hoursPassed < 24) {
        setIsLoading(false);
        return; // Skip location check if disclaimer was accepted
      }
    }

    const checkLocation = async () => {
      try {
        // STEP 1: Get the client's IP address first
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        const ip = ipData.ip;
        
        console.log("Detected client IP:", ip);
        
        // STEP 2: Try primary geolocation service with the IP
        try {
          const geoResponse = await fetch(
            `https://geo.multifarm.fi/v1/geo_info?ip=${ip}`
          );
          
          const geoData: GeoResponse = await geoResponse.json();
          
          // Check if we have city data from the primary source
          if (geoData.city?.names?.en) {
            if (
              geoData.city.names.en === "London" ||
              geoData.city.names.en === "Washington"
            ) {
              setLocation(geoData.city.names.en);
              setShouldShow(true);
            }
          } else {
            // STEP 3: Fall back to ipapi if primary service doesn't have city data
            try {
              const fallbackResponse = await fetch(`https://ipapi.co/${ip}/json/`);
              const fallbackData = await fallbackResponse.json();
              
              if (
                (fallbackData.city === "Washington" && fallbackData.region === "DC") ||
                fallbackData.city === "Washington DC" ||
                fallbackData.city === "London"
              ) {
                setLocation(fallbackData.city === "London" ? "London" : "Washington");
                setShouldShow(true);
              }
            } catch (fallbackError) {
              console.log("Fallback geolocation error:", fallbackError);
            }
          }
        } catch (geoError) {
          console.error("Primary geolocation service error:", geoError);
          
          // Try fallback directly if primary service fails completely
          try {
            const fallbackResponse = await fetch(`https://ipapi.co/${ip}/json/`);
            const fallbackData = await fallbackResponse.json();
            
            if (
              (fallbackData.city === "Washington" && fallbackData.region === "DC") ||
              fallbackData.city === "Washington DC" ||
              fallbackData.city === "London"
            ) {
              setLocation(fallbackData.city === "London" ? "London" : "Washington");
              setShouldShow(true);
            }
          } catch (fallbackError) {
            console.log("Fallback geolocation error:", fallbackError);
          }
        }
      } catch (ipError) {
        console.error("Failed to detect IP:", ipError);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkLocation();
  }, []);

  if (isLoading || !shouldShow) return null;

  const handleContinue = () => {
    localStorage.setItem('disclaimerAccepted', Date.now().toString());
    setShouldShow(false);
    onClose?.();
  };

  const handleLeave = () => {
    window.location.href = 'https://www.google.com';
  };

  return (
    <div className="geo-modal-overlay">
      <div className="geo-modal-container">
        <div className="geo-modal-content">
          <h2 className="geo-modal-title">
            {location === "Washington" ? "Region Restricted" : "Website Disclaimer"}
          </h2>

          <div className="geo-modal-message">
            {location === "Washington" ? (
              <>
                <p className="geo-modal-primary-text">
                  {disclaimerText[location]}
                </p>
                <p className="geo-modal-secondary-text">
                  Token sales are restricted in certain countries due to legal regulations
                </p>
              </>
            ) : (
              <p className="geo-modal-primary-text">
                {disclaimerText[location] || disclaimerText["Default"]}
              </p>
            )}
          </div>

          <div className="geo-modal-actions">
            <button
              onClick={handleLeave}
              className="geo-modal-button leave-button"
            >
              Leave Site
            </button>
            {location !== "Washington" && (
              <button
                onClick={handleContinue}
                className="geo-modal-button continue-button"
              >
                Continue to Site
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeoBlockModal; 